import { useState, useEffect } from 'react';
import { CodeInput } from '../CodeInput/CodeInput';
import { Button } from '../Button/Button';
import { Icon } from '../../icons/Icon';
import { formatCountdown } from '../../../lib/timeUtils';
import styles from './CodeConfirmationForm.module.scss';

export interface CodeConfirmationFormProps {
  /** Current code value */
  value?: string;
  /** Called when code changes */
  onChange?: (value: string) => void;
  /** Called when resend code is requested */
  // May resolve with the seconds to wait before the next resend.
  onResendCode?: () => Promise<number | void> | number | void;
  /** Whether the form is in loading state */
  isLoading?: boolean;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Number of code digits (default: 6) */
  length?: number;
  /** Initial countdown seconds for resend (default: 30) */
  resendDelay: number;
  /** Whether to auto-focus the first input */
  autoFocus?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Error message */
  error?: string | null;
}

export function CodeConfirmationForm({
  value = '',
  onChange,
  onResendCode,
  isLoading = false,
  disabled = false,
  length = 6,
  resendDelay,
  autoFocus = false,
  className,
  error = null,
}: CodeConfirmationFormProps) {
  const [countdown, setCountdown] = useState(resendDelay);

  // Countdown timer for resend code
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleCodeChange = (newValue: string) => {
    onChange?.(newValue);
  };

  const handleResendCode = async () => {
    if (countdown === 0 && onResendCode) {
      const nextDelay = await onResendCode();
      setCountdown(typeof nextDelay === 'number' ? nextDelay : resendDelay);
    }
  };

  const isResendDisabled = countdown > 0 || isLoading || disabled;

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <div className={styles.codeSection}>
        <CodeInput
          length={length}
          value={value}
          loading={isLoading}
          onChange={handleCodeChange}
          autoFocus={autoFocus}
          disabled={disabled}
          className={styles.codeInput}
          error={!!error}
        />
      </div>

      <div className={styles.resendSection}>
        <p className={styles.resendText}>
          {countdown > 0
            ? `Retrimite codul în ${formatCountdown(countdown)}`
            : 'Retrimite codul în 00:00'}
        </p>
        <Button
          trackingId="resend_code"
          onClick={handleResendCode}
          disabled={isResendDisabled}
          variant="text"
          size="small"
          leftIcon={
            <Icon
              name="refresh"
              style={{
                color: `${isResendDisabled ? 'var(--color-gray-15)' : 'var(--color-brand-100)'}`,
              }}
            />
          }
          className={styles.resendButton}
          text="Retrimite codul"
        />
      </div>
    </div>
  );
}
